/**
 * แก้ไขข้อมูลลูกค้า — `PATCH /v1/customers/{customer_id}` (D-125)
 *
 * **หน้าแก้ไขแยกของตัวเอง ไม่ใช่ modal** เปิดด้วย `?view=edit&customer=<id>` ผังหน้า
 * เหมือนหน้ารายละเอียด (`CustomerDetailView`) แต่ฟิลด์ที่แก้ได้กลายเป็นช่องกรอก:
 * ชื่อลูกค้า สถานะ ที่อยู่ และผู้ติดต่อ 1-3 ชุด — รหัสลูกค้ายังอ่านอย่างเดียวเหมือนเดิม
 * เพราะเป็น business key ที่แก้ไม่ได้ (D-089)
 *
 * **เลิกใช้กล่องแก้ไขเดิมของ `CustomerScreen` ไปเลย** ที่อยู่กับผู้ติดต่อหลายบรรทัด
 * ไม่พอดีกับกล่องที่ลอยทับตาราง และ URL ที่ bookmark/ส่งต่อได้คือสิ่งที่หน้ารายละเอียด
 * ต้องการอยู่แล้ว (D-123 ข้อ 6) — หน้าแก้ไขควรมีคุณสมบัติเดียวกัน
 *
 * **ผู้ติดต่อเป็นสามช่องตายตัว ไม่ใช่รายการที่เพิ่ม/ลบได้**: `contact_no` คือ 1-3
 * เสมอ (D-121) ชุดที่ไม่กรอกอะไรเลยไม่ถูกส่งไปกับคำขอ (`PATCH` แทนที่ทั้งชุด — ชุดที่
 * ไม่ส่งเท่ากับถูกลบ) ส่วนชุดที่กรอกโทร/อีเมลไว้แต่ไม่กรอกชื่อถูกกันไว้ที่ฟอร์มนี้เลย
 * เพราะคอลัมน์ `contact_name` เป็น `NOT NULL` ฝั่งฐานข้อมูล — ปล่อยให้มันไปตายที่
 * เซิร์ฟเวอร์แล้วอ่านไม่ออกว่าช่องไหนแพงกว่าบอกตั้งแต่ตรงนี้
 *
 * **แต่ละฟิลด์ระดับบนถูกส่งเมื่อเปลี่ยนจากค่าที่โหลดมาเท่านั้น** (เหมือน
 * `ProductEditView`) ยกเว้น `contacts` ซึ่งเมื่อถูกตัดสินว่า "เปลี่ยน" จะส่ง**ทั้ง
 * รายการ** เสมอ ไม่ใช่ผลต่าง — ตรงกับที่ `docs/API_CONTRACT.md` เขียนไว้ว่า `PATCH`
 * ของกลุ่มนี้แทนที่ทั้งชุด `address` ที่ถูกลบจนว่างส่งเป็น `null` จริง ๆ (ไม่ใช่ตัดคีย์
 * ทิ้ง) เพราะ schema ฝั่งเซิร์ฟเวอร์รับ `null` เป็นคำสั่ง "ล้างที่อยู่"
 *
 * **บันทึกสำเร็จ → นำทางกลับหน้ารายละเอียดของลูกค้ารายนั้นพร้อม router state**
 * `{ customerUpdated: true }` ที่ `CustomerDetailView` อ่านเพื่อแสดงข้อความสำเร็จ —
 * ไม่ใช่ banner ที่ค้างอยู่ในไฟล์นี้ เพราะหน้าที่ผู้ใช้เห็นหลังบันทึกคือหน้ารายละเอียด
 * ตาม WO ไม่ใช่หน้าแก้ไขเดิม ส่วนรายการ (`CustomerScreen`) ถูกสั่งให้โหลดใหม่ผ่าน
 * `onSaved()` เพื่อให้ตอนกลับไปหน้ารายการเห็นค่าล่าสุด ไม่ใช่ค่าที่ค้างในหน่วยความจำ
 *
 * ⚠️ `CustomerFailureNotice` ถูก import จาก `CustomerScreen` ซึ่งเป็นวงจร import
 * เดียวกับที่ `CustomerDetailView` อธิบายไว้แล้ว — ปลอดภัยด้วยเหตุผลเดียวกัน
 * (function declaration ที่ hoist ตอน instantiate และถูกเรียกตอน render เท่านั้น)
 *
 * **ส่วนผู้ติดต่อถูก export จากที่นี่ให้ `CustomerCreateView` ใช้ร่วม** (D-126 ข้อ 3):
 * `ContactFormState`, `contactSlots`, `validateContacts`, `buildContactsPayload`,
 * `problemText` และ component `CustomerContactFields` เป็นตรรกะและ markup เดียวที่
 * ทั้งสองหน้าเดินผ่าน — สองหน้าที่มีกฎผู้ติดต่อต่างกันคือบั๊กที่รอเกิด ไม่ใช่ไฟล์
 * component ใหม่เพราะ `write_scope` ของ WO นี้ไม่มีไฟล์นอกกลุ่มที่แก้อยู่แล้ว
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { CustomerFailureNotice } from './CustomerScreen';
import { InfoHint } from '../components/InfoHint';
import { StatusBadge } from '../StatusBadge';
import { fetchCustomer, updateCustomer, type CustomerFailure } from '../../lib/customers/client';
import {
  customerStatusLabel,
  customerStatusTone,
  offeredCustomerStatuses,
} from '../../lib/customers/customersView';
import {
  CUSTOMER_CONTACT_EMAIL_MAX_LENGTH,
  CUSTOMER_CONTACT_NAME_MAX_LENGTH,
  CUSTOMER_CONTACT_NO_MAXIMUM,
  CUSTOMER_CONTACT_PHONE_MAX_LENGTH,
  CUSTOMER_NAME_MAX_LENGTH,
  type Customer,
  type CustomerContact,
  type UpdateCustomerBody,
} from '../../lib/customers/types';

export interface CustomerEditViewProps {
  readonly customerId: string;
  /** query ของหน้ารายละเอียดที่ยกเลิก/บันทึกสำเร็จแล้วต้องกลับไป — รักษา sort/dir/page */
  readonly detailSearch: string;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** แจ้ง `CustomerScreen` ให้โหลดทะเบียนใหม่ (D-125) — เรียกก่อนนำทางกลับเสมอ */
  readonly onSaved: () => void;
}

/** ค่าที่เซิร์ฟเวอร์ยืนยันแล้ว — ไม่ใช่ค่าที่กำลังพิมพ์ */
type RecordState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly customer: Customer }
  | CustomerFailure;

type SaveState =
  | { readonly status: 'idle' }
  | { readonly status: 'saving' }
  | { readonly status: 'invalid-form'; readonly problems: readonly string[] }
  | CustomerFailure;

/** แชร์กับ `CustomerCreateView` (D-126 ข้อ 3) — หนึ่งช่องผู้ติดต่อที่กำลังกรอก */
export interface ContactFormState {
  readonly contact_no: number;
  readonly contact_name: string;
  readonly phone: string;
  readonly email: string;
}

interface EditFormState {
  readonly customer_name: string;
  readonly status: string;
  readonly address: string;
  /** เสมอสามชุด เรียงตาม `contact_no` 1 ถึง `CUSTOMER_CONTACT_NO_MAXIMUM` */
  readonly contacts: readonly ContactFormState[];
}

/** ตรวจรูปแบบอีเมลแบบเรียบง่าย — ไม่มี validator กลางของอีเมลใช้ร่วมกันในโปรเจกต์นี้ */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * สามชุดตายตัวเสมอ — ชุดที่เซิร์ฟเวอร์ไม่มีข้อมูลกลายเป็นช่องว่างให้กรอกใหม่
 *
 * แชร์กับ `CustomerCreateView` (D-126 ข้อ 3): เรียกด้วย `undefined` ตอนสร้างใหม่ —
 * ไม่มีลูกค้าอยู่ก่อน จึงได้สามช่องว่างเหมือนที่หน้าแก้ไขได้ตอนลูกค้าไม่มีผู้ติดต่อเลย
 */
export function contactSlots(
  contacts: readonly CustomerContact[] | undefined,
): readonly ContactFormState[] {
  const existing = contacts ?? [];
  return Array.from({ length: CUSTOMER_CONTACT_NO_MAXIMUM }, (_unused, index) => {
    const contactNo = index + 1;
    const found = existing.find((contact) => contact.contact_no === contactNo);
    return {
      contact_no: contactNo,
      contact_name: found?.contact_name ?? '',
      phone: found?.phone ?? '',
      email: found?.email ?? '',
    };
  });
}

/** ฟอร์มเริ่มต้นคือค่าที่เซิร์ฟเวอร์ยืนยันล่าสุด ไม่ใช่ค่าว่างหรือค่าที่ค้างไว้ */
function formFromCustomer(customer: Customer): EditFormState {
  return {
    customer_name: customer.customer_name,
    status: customer.status,
    address: customer.address ?? '',
    contacts: contactSlots(customer.contacts),
  };
}

/** แชร์กับ `CustomerCreateView` (D-126 ข้อ 3) */
export interface ContactProblem {
  readonly contact_no: number;
  readonly kind: 'missing-name' | 'invalid-email';
}

/**
 * ชุดที่กรอกโทร/อีเมลแต่ไม่กรอกชื่อ และชุดที่อีเมลผิดรูปแบบ — ทั้งสองกันไม่ให้บันทึก
 * (D-125) ชื่อเป็น `NOT NULL` ฝั่งฐานข้อมูล ปล่อยให้ไปตายที่เซิร์ฟเวอร์จะอ่านไม่ออกว่า
 * ช่องไหนที่พัง
 *
 * กฎเดียวกันนี้ใช้กับหน้าสร้าง (D-126 ข้อ 3) — สองหน้าที่มีกฎผู้ติดต่อต่างกันคือบั๊ก
 * ที่รอเกิด
 */
export function validateContacts(
  contacts: readonly ContactFormState[],
): readonly ContactProblem[] {
  const problems: ContactProblem[] = [];
  for (const contact of contacts) {
    const name = contact.contact_name.trim();
    const phone = contact.phone.trim();
    const email = contact.email.trim();
    if (name === '' && (phone !== '' || email !== '')) {
      problems.push({ contact_no: contact.contact_no, kind: 'missing-name' });
      continue;
    }
    if (email !== '' && !EMAIL_PATTERN.test(email)) {
      problems.push({ contact_no: contact.contact_no, kind: 'invalid-email' });
    }
  }
  return problems;
}

/** ชุดที่ไม่กรอกชื่อเลยไม่ถูกส่งไปกับคำขอ — ถือว่าช่องนั้นไม่มีผู้ติดต่อ (แชร์กับหน้าสร้าง) */
export function buildContactsPayload(
  contacts: readonly ContactFormState[],
): readonly CustomerContact[] {
  const payload: CustomerContact[] = [];
  for (const contact of contacts) {
    const name = contact.contact_name.trim();
    if (name === '') {
      continue;
    }
    const email = contact.email.trim();
    const phone = contact.phone.trim();
    payload.push({
      contact_no: contact.contact_no,
      contact_name: name,
      email: email === '' ? null : email,
      phone: phone === '' ? null : phone,
    });
  }
  return payload;
}

function contactsEqual(
  left: readonly CustomerContact[],
  right: readonly CustomerContact[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((contact, index) => {
    const other = right[index];
    return (
      other !== undefined &&
      contact.contact_no === other.contact_no &&
      contact.contact_name === other.contact_name &&
      contact.email === other.email &&
      contact.phone === other.phone
    );
  });
}

type BuildResult =
  | { readonly ok: true; readonly body: UpdateCustomerBody | null }
  | { readonly ok: false; readonly problems: readonly ContactProblem[] };

/**
 * ประกอบ body ของ `PATCH` — เฉพาะฟิลด์ที่เปลี่ยนจากค่าที่โหลดมา ยกเว้น `contacts`
 * ซึ่งเมื่อถูกตัดสินว่าเปลี่ยนจะพาไปทั้งรายการเสมอ (contract แทนที่ทั้งชุด ไม่ใช่
 * ผลต่างทีละ slot)
 *
 * `body: null` แปลว่า "ไม่มีอะไรเปลี่ยนเลย" — ผู้เรียกต้องไม่ยิงคำขอในกรณีนี้
 */
function buildPatchBody(original: Customer, form: EditFormState): BuildResult {
  const problems = validateContacts(form.contacts);
  if (problems.length > 0) {
    return { ok: false, problems };
  }

  const name = form.customer_name.trim();
  const status = form.status.trim();
  const addressValue = form.address.trim() === '' ? null : form.address.trim();
  const originalAddress = original.address ?? null;
  const originalContacts = buildContactsPayload(contactSlots(original.contacts));
  const nextContacts = buildContactsPayload(form.contacts);

  const body: UpdateCustomerBody = {
    ...(name !== '' && name !== original.customer_name ? { customer_name: name } : {}),
    ...(status !== '' && status !== original.status ? { status } : {}),
    ...(addressValue !== originalAddress ? { address: addressValue } : {}),
    ...(contactsEqual(nextContacts, originalContacts) ? {} : { contacts: nextContacts }),
  };

  const touched =
    body.customer_name !== undefined ||
    body.status !== undefined ||
    body.address !== undefined ||
    body.contacts !== undefined;

  return { ok: true, body: touched ? body : null };
}

export function problemText(problem: ContactProblem): string {
  return problem.kind === 'missing-name'
    ? `ผู้ติดต่อช่องที่ ${problem.contact_no}: ต้องกรอกชื่อก่อน เพราะมีโทรศัพท์หรืออีเมลอยู่`
    : `ผู้ติดต่อช่องที่ ${problem.contact_no}: อีเมลไม่ถูกรูปแบบ`;
}

export interface CustomerContactFieldsProps {
  /** ตัดหน้าของ `data-testid` — `customer-edit` หรือ `customer-create` */
  readonly testIdPrefix: string;
  readonly contacts: readonly ContactFormState[];
  readonly errors: ReadonlySet<number>;
  readonly onChange: (
    contactNo: number,
    field: Exclude<keyof ContactFormState, 'contact_no'>,
    value: string,
  ) => void;
}

/** สามช่องผู้ติดต่อ — markup เดียวที่หน้าแก้ไขและหน้าสร้างเดินผ่าน (D-126 ข้อ 3) */
export function CustomerContactFields({
  testIdPrefix,
  contacts,
  errors,
  onChange,
}: CustomerContactFieldsProps) {
  return (
    <section className="cap-card cap-card--form" aria-label="ผู้ติดต่อของลูกค้า">
      <div className="cap-card__head">
        <h2 className="cap-card__title">
          ผู้ติดต่อ
          <InfoHint
            label="เกี่ยวกับช่องผู้ติดต่อ"
            text="สามช่องเสมอ — ช่องที่ไม่กรอกอะไรเลยถือว่าไม่มีผู้ติดต่อชุดนั้น"
          />
        </h2>
      </div>
      {contacts.map((contact) => (
        <div
          className="cap-form__grid"
          key={contact.contact_no}
          data-testid={`${testIdPrefix}-contact-${contact.contact_no}`}
        >
          <label className="cap-field">
            <span className="cap-field__label">ผู้ติดต่อ {contact.contact_no} — ชื่อ</span>
            <input
              className="cap-input"
              data-testid={`${testIdPrefix}-contact-${contact.contact_no}-name`}
              value={contact.contact_name}
              maxLength={CUSTOMER_CONTACT_NAME_MAX_LENGTH}
              autoComplete="off"
              aria-invalid={errors.has(contact.contact_no)}
              onChange={(event) => onChange(contact.contact_no, 'contact_name', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">โทรศัพท์</span>
            <input
              className="cap-input"
              data-testid={`${testIdPrefix}-contact-${contact.contact_no}-phone`}
              value={contact.phone}
              maxLength={CUSTOMER_CONTACT_PHONE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => onChange(contact.contact_no, 'phone', event.target.value)}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">อีเมล</span>
            <input
              className="cap-input"
              type="email"
              data-testid={`${testIdPrefix}-contact-${contact.contact_no}-email`}
              value={contact.email}
              maxLength={CUSTOMER_CONTACT_EMAIL_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => onChange(contact.contact_no, 'email', event.target.value)}
            />
          </label>
          {errors.has(contact.contact_no) ? (
            <p
              className="cap-field__hint cap-field__hint--error"
              data-testid={`${testIdPrefix}-contact-${contact.contact_no}-error`}
            >
              ต้องกรอกชื่อก่อน ถ้ามีโทรศัพท์หรืออีเมลอยู่ในชุดนี้
            </p>
          ) : null}
        </div>
      ))}
    </section>
  );
}

export function CustomerEditView({
  customerId,
  detailSearch,
  apiBaseUrl,
  fetchImpl,
  onSaved,
}: CustomerEditViewProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [reloadToken, setReloadToken] = useState(0);
  const [record, setRecord] = useState<RecordState>({ status: 'loading' });
  const [form, setForm] = useState<EditFormState | null>(null);
  const [save, setSave] = useState<SaveState>({ status: 'idle' });
  const [contactErrors, setContactErrors] = useState<ReadonlySet<number>>(new Set());

  // ผลของ request ที่กลับมาหลัง component ถูกถอดออกแล้วต้องไม่ถูกเขียนลง state
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setRecord({ status: 'loading' });
    setForm(null);
    setSave({ status: 'idle' });
    setContactErrors(new Set());

    void fetchCustomer(customerId, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setRecord(result);
      if (result.status === 'ready') {
        setForm(formFromCustomer(result.customer));
      }
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [customerId, apiBaseUrl, fetchImpl, reloadToken]);

  const customer = record.status === 'ready' ? record.customer : null;

  const updateField = (field: 'customer_name' | 'status' | 'address', value: string) => {
    setForm((current) => (current === null ? current : { ...current, [field]: value }));
  };

  const updateContact = (
    contactNo: number,
    field: Exclude<keyof ContactFormState, 'contact_no'>,
    value: string,
  ) => {
    setForm((current) =>
      current === null
        ? current
        : {
            ...current,
            contacts: current.contacts.map((contact) =>
              contact.contact_no === contactNo ? { ...contact, [field]: value } : contact,
            ),
          },
    );
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (form === null || save.status === 'saving' || customer === null) {
      return;
    }

    const built = buildPatchBody(customer, form);
    if (!built.ok) {
      setContactErrors(new Set(built.problems.map((problem) => problem.contact_no)));
      setSave({ status: 'invalid-form', problems: built.problems.map(problemText) });
      return;
    }
    setContactErrors(new Set());

    if (built.body === null) {
      return;
    }

    setSave({ status: 'saving' });
    const result = await updateCustomer(customerId, built.body, { apiBaseUrl, fetchImpl });
    if (!mounted.current) {
      return;
    }

    if (result.status === 'ready') {
      // ลำดับสำคัญ: แจ้งทะเบียนให้โหลดใหม่ก่อนนำทางออกจากหน้านี้
      onSaved();
      navigate({ pathname, search: detailSearch }, { state: { customerUpdated: true } });
      return;
    }

    // ⚠️ ไม่แตะ `record`/`form`: ความล้มเหลวไม่ใช่การบันทึก สิ่งที่พิมพ์ไว้ยังอยู่ให้แก้ต่อได้
    setSave(result);
  };

  const saveFailure: CustomerFailure | null =
    save.status === 'idle' || save.status === 'saving' || save.status === 'invalid-form'
      ? null
      : save;

  const nameEmpty = form !== null && form.customer_name.trim() === '';
  const submitDisabled = save.status === 'saving' || form === null || nameEmpty;

  return (
    <div className="cap-page" data-testid="customer-edit">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title" data-testid="customer-edit-code">
            {customer === null ? 'แก้ไขข้อมูลลูกค้า' : customer.customer_code}
          </h1>
          <p className="cap-page__sub">
            {customer === null ? `รหัสภายใน ${customerId}` : customer.customer_name}
          </p>
        </div>
        <div className="cap-page__actions">
          {customer === null ? null : (
            <StatusBadge tone={customerStatusTone(customer.status)}>
              {customerStatusLabel(customer.status)}
            </StatusBadge>
          )}
          <Link
            className="cap-button cap-button--ghost"
            data-testid="customer-edit-cancel"
            to={{ pathname, search: detailSearch }}
          >
            ยกเลิก
          </Link>
        </div>
      </div>

      {record.status === 'loading' ? (
        <div className="cap-notice" data-testid="customer-edit-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดข้อมูลลูกค้าเพื่อแก้ไข…</p>
        </div>
      ) : null}

      {record.status !== 'loading' && record.status !== 'ready' ? (
        <CustomerFailureNotice
          failure={record}
          context="detail"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {save.status === 'invalid-form' ? (
        <div
          className="cap-banner cap-banner--warning"
          role="status"
          data-testid="customer-edit-invalid"
        >
          <strong>ยังบันทึกไม่ได้ — ตรวจผู้ติดต่อก่อน</strong>
          <span>{save.problems.join(' · ')}</span>
        </div>
      ) : null}

      {saveFailure === null ? null : (
        <CustomerFailureNotice failure={saveFailure} context="edit" />
      )}

      {customer === null || form === null ? null : (
        <form
          className="cap-form"
          data-testid="customer-edit-form"
          aria-label="แก้ไขข้อมูลลูกค้า"
          onSubmit={(event) => void onSubmit(event)}
          noValidate
        >
          <section className="cap-card cap-card--form" aria-label="ข้อมูลลูกค้า">
            <div className="cap-form__grid">
              {/**
               * รหัสลูกค้าเป็น **ข้อความ ไม่ใช่ช่องกรอกที่ disabled** โดยตั้งใจ — ดูเหตุผล
               * เดียวกับกล่องแก้ไขเดิมที่ `CustomerScreen` (D-089)
               */}
              <div className="cap-field">
                <span className="cap-field__label">
                  รหัสลูกค้า
                  <InfoHint
                    label="เกี่ยวกับรหัสลูกค้า"
                    text={
                      <>
                        <strong>แก้ไม่ได้</strong> — เป็น business key ที่ใบสั่งขาย lot และการ
                        ส่งของอ้างอยู่ (D-089) คำขอนี้จึงไม่ส่งรหัสไปด้วยเลย
                      </>
                    }
                  />
                </span>
                <p data-testid="customer-edit-code-field">
                  <code className="cap-table__code">{customer.customer_code}</code>
                </p>
              </div>

              <label className="cap-field">
                <span className="cap-field__label">ชื่อลูกค้า</span>
                <input
                  className="cap-input"
                  name="customer_name"
                  data-testid="customer-edit-name"
                  value={form.customer_name}
                  maxLength={CUSTOMER_NAME_MAX_LENGTH}
                  autoComplete="off"
                  onChange={(event) => updateField('customer_name', event.target.value)}
                />
              </label>

              <label className="cap-field">
                <span className="cap-field__label">
                  สถานะ
                  <InfoHint
                    label="เกี่ยวกับสถานะลูกค้า"
                    text={
                      <>
                        เลิกค้าขายกับลูกค้ารายนี้คือการตั้งสถานะเป็น INACTIVE —{' '}
                        <strong>ไม่มีการลบ</strong> เพราะเอกสารที่อ้างเขาอยู่ต้องตามสอบได้ต่อไป
                      </>
                    }
                  />
                </span>
                <select
                  className="cap-input"
                  name="status"
                  data-testid="customer-edit-status"
                  value={form.status}
                  onChange={(event) => updateField('status', event.target.value)}
                >
                  {offeredCustomerStatuses(customer.status).map((code) => (
                    <option key={code} value={code}>
                      {customerStatusLabel(code)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="cap-field">
                <span className="cap-field__label">
                  ที่อยู่
                  <InfoHint
                    label="เกี่ยวกับช่องที่อยู่"
                    text="เว้นว่างเพื่อล้างที่อยู่ที่เคยบันทึกไว้ — ขึ้นบรรทัดใหม่ได้ตามต้องการ"
                  />
                </span>
                <textarea
                  className="cap-input"
                  name="address"
                  data-testid="customer-edit-address"
                  rows={4}
                  value={form.address}
                  onChange={(event) => updateField('address', event.target.value)}
                />
              </label>
            </div>
          </section>

          <CustomerContactFields
            testIdPrefix="customer-edit"
            contacts={form.contacts}
            errors={contactErrors}
            onChange={updateContact}
          />

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="customer-edit-submit"
              disabled={submitDisabled}
            >
              {save.status === 'saving' ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
